import { useAtomValue } from 'jotai';
import { Button } from '@carbon/react';
import { Location, LocationFilled } from '@carbon/react/icons';
import { useDatasetAtoms } from '../dataset-context';

interface ShowOnMapToggleProps {
  /** The row's highlight id, as the dataset's highlightRowKey reads it. */
  rowId: string;
  /** The row has no footprint to show. */
  disabled: boolean;
  onShow: () => void;
  onClear: () => void;
}

/**
 * "Show on map" for datasets that toggle it (showOnMapToggles): on the highlighted
 * row it reads "Remove from map" and clears the highlight. It reads the highlight
 * itself, so selecting a row doesn't rebuild the table's columns.
 * @param {ShowOnMapToggleProps} props The row and its show/clear callbacks.
 * @return {JSX.Element} The toggle.
 */
export function ShowOnMapToggle({
  rowId,
  disabled,
  onShow,
  onClear,
}: ShowOnMapToggleProps): JSX.Element {
  const atoms = useDatasetAtoms();
  const selected = useAtomValue(atoms.highlightedItemIdAtom) === rowId;
  let label = 'Show on map';
  if (disabled) label = 'No map footprint to show';
  else if (selected) label = 'Remove from map';
  return (
    <Button
      kind="ghost"
      size="sm"
      renderIcon={selected ? LocationFilled : Location}
      iconDescription={label}
      hasIconOnly
      isSelected={selected}
      aria-pressed={selected}
      disabled={disabled}
      onClick={selected ? onClear : onShow}
    />
  );
}
